import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { authGuard, guestGuard } from './core/authentication/auth.guard';
import { type PermissionRouteData, permissionGuard } from './core/authorization/permission.guard';
import { pendingChangesGuard } from './core/navigation/pending-changes.guard';
import { vacationGuard } from './features/vacations/vacation.guard';
import { captureWeekGuard } from './features/payroll/capture/capture-week.guard';

export const APPLICATION_TITLE = 'PreNómina';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./features/authentication/login/login').then((component) => component.Login),
    title: APPLICATION_TITLE,
  },
  {
    path: 'select-tenant',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./features/platform/select-tenant/select-tenant').then(
        (component) => component.SelectTenant,
      ),
    title: APPLICATION_TITLE,
  },
  {
    path: 'select-context',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./features/platform/select-enterprise-context/select-enterprise-context').then(
        (component) => component.SelectEnterpriseContext,
      ),
    title: APPLICATION_TITLE,
  },
  {
    path: 'access-denied',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () =>
      import('./features/authorization/access-denied/access-denied').then(
        (component) => component.AccessDenied,
      ),
    title: APPLICATION_TITLE,
  },
  {
    path: 'employees',
    canActivate: [authGuard, permissionGuard],
    canDeactivate: [pendingChangesGuard],
    runGuardsAndResolvers: 'always',
    data: { permission: 'empleados.ver' } satisfies PermissionRouteData,
    loadComponent: () => import('./features/employees/employees-page').then((c) => c.EmployeesPage),
    title: APPLICATION_TITLE,
  },
  {
    path: 'payroll/periods/:periodId/destinations',
    canActivate: [captureWeekGuard],
    canDeactivate: [pendingChangesGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () => import('./features/payroll/capture/payroll-destinations-page').then(c => c.PayrollDestinationsPage),
    title: APPLICATION_TITLE,
  },
  {
    path: 'vacations',
    canActivate: [authGuard, vacationGuard],
    canDeactivate: [pendingChangesGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () => import('./features/vacations/vacations-page').then(c => c.VacationsPage),
    title: APPLICATION_TITLE,
  },
  {
    path: 'payroll/periods',
    canActivate: [authGuard, permissionGuard],
    canDeactivate: [pendingChangesGuard],
    runGuardsAndResolvers: 'always',
    data: { permission: 'prenomina.periodos.ver' } satisfies PermissionRouteData,
    loadComponent: () =>
      import('./features/payroll/periods/payroll-periods-page').then((c) => c.PayrollPeriodsPage),
    title: APPLICATION_TITLE,
  },
  {
    path: 'labor-catalogs',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: 'employer-registrations',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: { permission: 'catalogos_laborales.ver' } satisfies PermissionRouteData,
        loadComponent: () =>
          import('./features/labor-catalogs/employer-registrations/employer-registrations-page').then(
            (component) => component.EmployerRegistrationsPage,
          ),
        title: APPLICATION_TITLE,
      },
      {
        path: 'contractors',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: { permission: 'catalogos_laborales.ver' } satisfies PermissionRouteData,
        loadComponent: () =>
          import('./features/labor-catalogs/contractors/contractors-page').then(
            (component) => component.ContractorsPage,
          ),
        title: APPLICATION_TITLE,
      },
      {
        path: 'projects',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: {
          permission: 'catalogos_laborales.ver',
          catalogKind: 'project',
        } satisfies PermissionRouteData & { catalogKind: 'project' },
        loadComponent: () =>
          import('./features/labor-catalogs/organizational-catalogs/organizational-catalog-page').then(
            (component) => component.OrganizationalCatalogPage,
          ),
        title: APPLICATION_TITLE,
      },
      {
        path: 'departments',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: {
          permission: 'catalogos_laborales.ver',
          catalogKind: 'department',
        } satisfies PermissionRouteData & { catalogKind: 'department' },
        loadComponent: () =>
          import('./features/labor-catalogs/organizational-catalogs/organizational-catalog-page').then(
            (component) => component.OrganizationalCatalogPage,
          ),
        title: APPLICATION_TITLE,
      },
    ],
  },
  {
    path: 'labor-configuration',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: 'work-schedules',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: { permission: 'configuracion_laboral.ver' } satisfies PermissionRouteData,
        loadComponent: () =>
          import('./features/labor-configuration/work-schedules/work-schedules-page').then(
            (component) => component.WorkSchedulesPage,
          ),
        title: APPLICATION_TITLE,
      },
      {
        path: 'overtime',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: { permission: 'configuracion_laboral.ver' } satisfies PermissionRouteData,
        loadComponent: () =>
          import('./features/labor-configuration/overtime/overtime-page').then(
            (component) => component.OvertimePage,
          ),
        title: APPLICATION_TITLE,
      },
    ],
  },
  {
    path: 'administration',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: 'users',
        canActivate: [permissionGuard],
        runGuardsAndResolvers: 'always',
        data: { permission: 'usuarios.ver' } satisfies PermissionRouteData,
        loadComponent: () =>
          import('./features/administration/users/users-page').then(
            (component) => component.UsersPage,
          ),
        title: APPLICATION_TITLE,
      },
      {
        path: 'roles',
        canActivate: [permissionGuard],
        canDeactivate: [pendingChangesGuard],
        runGuardsAndResolvers: 'always',
        data: { permission: 'roles.ver' } satisfies PermissionRouteData,
        loadComponent: () =>
          import('./features/administration/roles/roles-page').then(
            (component) => component.RolesPage,
          ),
        title: APPLICATION_TITLE,
      },
    ],
  },
  {
    path: '',
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    loadComponent: () => import('./features/home/home/home').then((component) => component.Home),
    title: APPLICATION_TITLE,
  },
  ...(environment.production
    ? []
    : [
        {
          path: 'technical/ux-components',
          canActivate: [authGuard],
          runGuardsAndResolvers: 'always' as const,
          loadComponent: () =>
            import('./features/technical/ux-overlay-demo/ux-overlay-demo').then(
              (component) => component.UxOverlayDemo,
            ),
          title: APPLICATION_TITLE,
        },
      ]),
  { path: '**', redirectTo: '' },
];
